import classNames from 'classnames';
import { observer } from 'mobx-react';
import { useId, useRef, useState } from 'react';

import { StoreData } from '../../../../app/data/model';
import { StackedWarnings } from '../../../../app/layout/Warnings/StackedWarnings';
import TextButton from '../../../../app/shared/components/Button/TextButton';
import Icon from '../../../../app/shared/components/Icon/Icon';
import useOnClickOutside from '../../../../app/shared/hooks/useOnClickOutside';
import useOnKeyDown from '../../../../app/shared/hooks/useOnKeyDown';
import { Authorize } from '../../../authentication/Authorize';
import { FlokkUserPolicy } from '../../../Policy';
import StoreDisplayName from '../StoreDisplayName/StoreDisplayName';
import StoreChangeFailedWarning from './StoreCannotBeChangedWarning/StoreChangeFailedWarning';
import css from './StoreSelector.css';
import StoreSelectorOptions from './StoreSelectorOptions/StoreSelectorOptions';
import StoreSelectorSearch from './StoreSelectorSearch/StoreSelectorSearch';
import StoreSelectorState from './StoreSelectorState';

interface OrganizationSelectorProps {
  state: StoreSelectorState;
  className?: string;
}

const StoreSelector = ({ className, state }: OrganizationSelectorProps) => {
  const menuId = useId();
  const ref = useRef<HTMLDivElement>(null);
  const [menuEl, setMenuEl] = useState<HTMLDivElement | null>(null);

  useOnClickOutside(ref, state.close);
  useOnKeyDown('Escape', state.close);

  return (
    <div className={classNames(css.element, className)} ref={ref}>
      <TextButton
        className={css.button}
        loaderClassName={css.loader}
        childrenClassName={css.inner}
        loaderPosition="center"
        type="button"
        aria-controls={menuId}
        aria-expanded={state.isOpen}
        aria-haspopup="true"
        onClick={state.toggle}
        isLoading={state.changeStoreCommand.processing}
        disabled={state.changeStoreCommand.disabled || state.changeStoreCommand.processing}
      >
        <StoreDisplayName
          className={css.displayName}
          storeId={state.currentStore.id}
          storeName={state.currentStore.displayName}
        />
        <Icon className={css.icon} name={state.isOpen ? 'angleUp' : 'angleDown'} />
      </TextButton>
      <div ref={setMenuEl} id={menuId} className={classNames(css.options, { [css.expanded]: state.isOpen })}>
        <Authorize policy={FlokkUserPolicy}>
          <StoreSelectorSearch state={state} popoverContainer={menuEl} />
        </Authorize>
        <div role="menu" className={css.storeList}>
          <StoreSelectorOptions
            options={state.stores}
            onClick={(storeData: StoreData) => {
              state.selectStore(storeData.id);
            }}
          />
        </div>
      </div>

      <StackedWarnings>
        <StoreChangeFailedWarning
          state={state.storeChangeFailedWarning}
          translation={state.translation.storeChangeFailedWarning}
        />
      </StackedWarnings>
    </div>
  );
};

export default observer(StoreSelector);
